import type { BrandKit, BrandKitInput } from '@live-stream/shared'
import type { HttpClient } from '../http.js'
import { putWithProgress } from '../meeting/attachments.js'

/**
 * The signed-in person's brand kit (E2d): their logo, colours and title
 * font, saved once and ready in every edit.
 */
export class Brand {
  constructor(private readonly http: HttpClient) {}

  /** The kit: the logo (a link to it, and its size), colours and font. */
  async get(): Promise<BrandKit> {
    return (await this.http.get<{ brand: BrandKit }>('/brand')).brand
  }

  /** Save the colours and font. */
  async save(input: BrandKitInput): Promise<BrandKit> {
    return (await this.http.put<{ brand: BrandKit }>('/brand', input)).brand
  }

  /**
   * A new logo (PNG, JPEG or WEBP): put straight into storage (with
   * progress, 0–1), then looked at by the server, in place of the one
   * before. Refused when it is not a picture it can read.
   */
  async uploadLogo(
    file: File,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ): Promise<BrandKit> {
    const begun = await this.http.post<{
      key: string
      upload: { url: string; headers: Record<string, string> }
    }>('/brand/logo', { type: file.type, size: file.size })
    await putWithProgress({
      url: begun.upload.url,
      headers: begun.upload.headers,
      body: file,
      onProgress: opts.onProgress,
      signal: opts.signal,
    })
    return (await this.http.post<{ brand: BrandKit }>('/brand/logo/done', { key: begun.key })).brand
  }

  /** Take the logo away (videos that use it keep their own copy). */
  async removeLogo(): Promise<BrandKit> {
    return (await this.http.delete<{ brand: BrandKit }>('/brand/logo')).brand
  }
}
